/**
 * Органические поля: то, что растекается.
 */

import { boxBlur, fbm, median3, mix, normalize, scatter, valueNoise } from '../core/noise'
import type { FieldFn } from './types'

/**
 * Порог по фрактальному полю.
 *
 * Проявляется везде сразу, пятнами неправильной формы. Радиальная добавка
 * задаёт, где начнётся раньше; её вес против шума — главная ручка:
 * много — послушная круглая клякса, мало — проявление без центра вообще.
 */
export const threshold: FieldFn = ({ w, h, rnd }) => {
  const noise = fbm(w, h, rnd, { octaves: 3, cells: 3, gain: 0.5 })
  const ox = 0.2 + rnd() * 0.6
  const oy = 0.2 + rnd() * 0.6
  const weight = 0.34 + rnd() * 0.22

  const radial = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    const dy = (y / h - oy) * 0.85
    for (let x = 0; x < w; x++) {
      const dx = x / w - ox
      radial[y * w + x] = Math.hypot(dx, dy)
    }
  }

  // Размывается ПОЛЕ, а не кадры: порог по сглаженному полю даёт плавную
  // границу, которая переживает увеличение маски. Порог по резкому полю
  // даёт ступеньку, и увеличение раздувает её в блок.
  return boxBlur(normalize(mix(noise, normalize(radial), weight)), w, h, 1)
}

/**
 * Проявитель: несколько пятен с разным временем старта.
 *
 * Каждое пятно всплывает в своё время и растёт со своей скоростью. Ощущение
 * не «фронт идёт», а «проступает в нескольких местах сразу» — как бумага
 * в ванночке с проявителем.
 */
export const patches: FieldFn = ({ w, h, rnd }) => {
  const count = 4 + Math.floor(rnd() * 5)
  const spots = scatter(count, rnd, 0.1).map((p) => ({
    ...p,
    // Время старта и скорость независимы: иначе все пятна выглядят
    // одинаковыми, просто сдвинутыми во времени.
    start: rnd() * 0.55,
    speed: 0.9 + rnd() * 1.5,
  }))

  const out = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    const fy = y / h
    for (let x = 0; x < w; x++) {
      const fx = x / w
      let best = Infinity
      for (const s of spots) {
        const d = Math.hypot(fx - s.x, (fy - s.y) * 0.9)
        const t = s.start + d / s.speed
        if (t < best) best = t
      }
      out[y * w + x] = best
    }
  }

  // Шум подмешивается ПОСЛЕ, а не в расстояние: иначе пятна перестают быть
  // пятнами и превращаются в тот же фрактальный порог.
  const grain = boxBlur(valueNoise(w, h, 7, rnd), w, h, 1)
  return boxBlur(normalize(mix(normalize(out), grain, 0.12)), w, h, 1)
}

/**
 * Капиллярное растекание: волновой обход по полю сопротивления.
 *
 * Единственный механизм с НАПРАВЛЕННЫМ распространением: фронт откуда-то
 * идёт и упирается в неоднородности бумаги, обтекая плотные места. Это и есть
 * тушь, всасываемая волокном, — то, чего пороговое проявление не даёт
 * в принципе.
 *
 * Реализация — алгоритм Дейкстры с очередью на корзинах (Dial). С обычной
 * двоичной кучей тот же результат стоит вчетверо дороже и вылезает из бюджета:
 * ключи здесь достаются в неубывающем порядке, поэтому курсор по корзинам
 * никогда не идёт назад и извлечение честно постоянное по времени.
 *
 * Стоимости целые, диагональ считается по метрике 5-7: это классическое
 * приближение евклидова расстояния с ошибкой около 2%, зато без чисел
 * с плавающей точкой в самом горячем цикле.
 */
export const capillary: FieldFn = ({ w, h, rnd }) => {
  const n = w * h

  // Поле сопротивления: чем выше контраст, тем жилистее растекание.
  // При малом получается круглая гладкая клякса, при большом — каналы.
  const contrast = 1.5 + rnd() * 3
  const noise = fbm(w, h, rnd, { octaves: 2, cells: 4, gain: 0.55 })
  const cost = new Uint8Array(n)
  for (let i = 0; i < n; i++) cost[i] = 1 + Math.round(noise[i]! * contrast)

  const STRAIGHT = 5
  const DIAG = 7
  const maxEdge = (1 + Math.ceil(contrast)) * DIAG
  const maxDist = (w + h) * maxEdge

  const dist = new Int32Array(n).fill(-1)
  const bucketHead = new Int32Array(maxDist + 1).fill(-1)
  const nextInBucket = new Int32Array(n).fill(-1)

  const push = (node: number, d: number): void => {
    nextInBucket[node] = bucketHead[d]!
    bucketHead[d] = node
  }

  for (const seed of scatter(2 + Math.floor(rnd() * 5), rnd, 0.12)) {
    const sx = Math.min(w - 1, Math.max(0, Math.round(seed.x * w)))
    const sy = Math.min(h - 1, Math.max(0, Math.round(seed.y * h)))
    const idx = sy * w + sx
    dist[idx] = 0
    push(idx, 0)
  }

  let reached = 0
  let maxSeen = 0

  for (let d = 0; d <= maxDist; d++) {
    let node = bucketHead[d]!
    while (node !== -1) {
      const nxt = nextInBucket[node]!
      // Ленивое удаление: узел мог попасть в несколько корзин, годится
      // только запись, совпадающая с текущим расстоянием.
      if (dist[node] === d) {
        reached++
        if (d > maxSeen) maxSeen = d

        const x = node % w
        const y = (node / w) | 0
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy
          if (yy < 0 || yy >= h) continue
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue
            const xx = x + dx
            if (xx < 0 || xx >= w) continue

            const to = yy * w + xx
            const step = (dx === 0 || dy === 0 ? STRAIGHT : DIAG) * cost[to]!
            const nd = d + step
            if (nd <= maxDist && (dist[to] === -1 || nd < dist[to]!)) {
              dist[to] = nd
              push(to, nd)
            }
          }
        }
      }
      node = nxt
    }
  }

  const out = new Float32Array(n)
  const scale = maxSeen || 1
  for (let i = 0; i < n; i++) {
    // Недостижимого быть не должно — сетка связная, — но если поле
    // выродилось, такие точки открываются последними, а не никогда.
    out[i] = dist[i] === -1 ? 1 : dist[i]! / scale
  }

  // Медиана убирает ступеньки метрики 5-7, не нарушая вложенности кадров.
  return reached > 0 ? boxBlur(median3(out, w, h), w, h, 1) : out
}
